import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDismissable } from '../../lib/hooks';
import { initials } from '../../lib/format';
import { BellIcon, LogoutIcon, SearchIcon } from '../icons/Icons';
import { useAuth } from '../../features/auth/AuthProvider';
import type { UserRole } from '../../types/api';

interface TopbarProps {
  title: string;
}

/** Labels for the role chip in the account menu. Mirrors the backend enum. */
const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
};

/** 56px header: page title on the left, notification / search / account on the right. */
export function Topbar({ title }: TopbarProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useDismissable(open, () => setOpen(false));

  const displayName = user?.name ?? 'Signed out';
  const avatarLabel = initials(displayName) || '?';

  const handleSignOut = () => {
    setOpen(false);
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <header className="topbar">
      <h1 className="topbar__title">{title}</h1>

      <div className="topbar__actions">
        <button type="button" className="icon-button" aria-label="Notifications">
          <BellIcon size={18} />
        </button>
        <button type="button" className="icon-button" aria-label="Search">
          <SearchIcon size={18} />
        </button>

        <div className="menu-anchor" ref={menuRef}>
          <button
            type="button"
            className="topbar__user"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={`Account menu for ${displayName}`}
            title={displayName}
            onClick={() => setOpen((value) => !value)}
          >
            <span className="topbar__avatar" aria-hidden="true">
              {avatarLabel}
            </span>
          </button>

          {open ? (
            <div className="menu menu--user" role="menu">
              <div className="menu__header">
                <p className="menu__name">{displayName}</p>
                {user ? <p className="menu__email">{user.email}</p> : null}
                {user ? <span className="menu__role">{ROLE_LABELS[user.role]}</span> : null}
              </div>
              <div className="menu__separator" role="separator" />
              <button
                type="button"
                role="menuitem"
                className="menu__item menu__item--danger"
                onClick={handleSignOut}
              >
                <LogoutIcon size={16} />
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

